import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  alpha,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ILaboratorySummary } from 'models/user';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Past this many laboratories a search box earns its place.
const SEARCH_THRESHOLD = 5;

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter((word) => word.length > 2)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || name.slice(0, 2).toUpperCase();

interface LaboratoryOptionProps {
  laboratory: ILaboratorySummary;
  current: boolean;
  selecting: boolean;
  disabled: boolean;
  onSelect: (id: string) => void;
}

const LaboratoryOption: FC<LaboratoryOptionProps> = ({
  laboratory,
  current,
  selecting,
  disabled,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <ButtonBase
      disabled={disabled || current}
      onClick={() => onSelect(laboratory.id)}
      aria-label={laboratory.companyName}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        textAlign: 'left',
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create(
          ['transform', 'box-shadow', 'border-color'],
          { duration: 180 },
        ),
        '& .arrow': {
          color: theme.palette.text.disabled,
          transition: theme.transitions.create(['transform', 'color'], {
            duration: 180,
          }),
        },
        '&:hover, &.Mui-focusVisible': {
          transform: 'translateY(-2px)',
          borderColor: theme.palette.primary.main,
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.16)}`,
          '& .arrow': {
            color: theme.palette.primary.main,
            transform: 'translateX(4px)',
          },
        },
        '&.Mui-disabled': {
          opacity: selecting || current ? 1 : 0.55,
          ...(current && {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }),
        },
      })}
    >
      <Avatar
        variant="rounded"
        sx={(theme) => ({
          width: 52,
          height: 52,
          fontWeight: 700,
          fontSize: theme.typography.pxToRem(17),
          color: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 2.5,
        })}
      >
        {initialsOf(laboratory.companyName)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h5"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.3,
          }}
        >
          {laboratory.companyName}
        </Typography>
        <Chip
          size="small"
          label={`${t('Code')} ${laboratory.code}`}
          sx={{ mt: 0.75, height: 22, fontWeight: 600 }}
        />
      </Box>

      {selecting ? (
        <CircularProgress size={22} />
      ) : current ? (
        <CheckCircleRoundedIcon color="primary" />
      ) : (
        <ArrowForwardRoundedIcon className="arrow" />
      )}
    </ButtonBase>
  );
};

interface LaboratoryPickerProps {
  laboratories: ILaboratorySummary[];
  /** Laboratory the session is already on, shown as selected. */
  currentId?: string | null;
  selectingId: string | null;
  onSelect: (id: string) => void;
  /** Columns on wide screens. */
  columns?: 1 | 2;
  /** Max height of the list before it scrolls. */
  maxListHeight?: number | string;
}

/** Searchable list of laboratories, shared by the login step and the switcher. */
const LaboratoryPicker: FC<LaboratoryPickerProps> = ({
  laboratories,
  currentId = null,
  selectingId,
  onSelect,
  columns = 2,
  maxListHeight,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return laboratories;
    return laboratories.filter(
      (laboratory) =>
        laboratory.companyName.toLowerCase().includes(term) ||
        String(laboratory.code).includes(term),
    );
  }, [laboratories, search]);

  return (
    <>
      {laboratories.length >= SEARCH_THRESHOLD && (
        <TextField
          fullWidth
          autoFocus
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('Search by name or code')}
          disabled={Boolean(selectingId)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: columns === 1 || laboratories.length === 1 ? '1fr' : 'repeat(2, 1fr)',
          },
          ...(maxListHeight && {
            maxHeight: maxListHeight,
            overflowY: 'auto',
            // room for the hover lift and shadow so they aren't clipped
            p: 0.5,
            m: -0.5,
          }),
        }}
      >
        {visible.map((laboratory) => (
          <LaboratoryOption
            key={laboratory.id}
            laboratory={laboratory}
            current={laboratory.id === currentId}
            selecting={selectingId === laboratory.id}
            disabled={Boolean(selectingId)}
            onSelect={onSelect}
          />
        ))}
      </Box>

      {visible.length === 0 && (
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          {t('No laboratory found')}
        </Typography>
      )}
    </>
  );
};

export default LaboratoryPicker;
